import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RemoveIcon } from "@/components/Icons";
import { decimalSeparator } from "@/lib/formatting";
import type { PlayerDraft } from "@/hooks/usePlayersState";

interface PlayerBuyInRowProps {
  player: PlayerDraft;
  rowLabel: number;
  currencySymbol: string;
  cashInputValue: (playerId: string, storedRawValue: string) => string;
  beginCashEdit: (playerId: string, currentRawValue: string) => void;
  changeCashEdit: (playerId: string, nextValue: string) => void;
  endCashEdit: () => void;
  chipInputValue: (playerId: string, field: "chipsBought" | "finalChips", storedRawValue: string) => string;
  beginChipEdit: (playerId: string, field: "chipsBought" | "finalChips", currentRawValue: string) => void;
  changeChipEdit: (playerId: string, field: "chipsBought" | "finalChips", nextValue: string) => void;
  endChipEdit: (playerId: string, field: "chipsBought" | "finalChips") => void;
  updatePlayerName: (id: string, name: string) => void;
  removePlayer: (id: string) => void;
}

export function PlayerBuyInRow({
  player,
  rowLabel,
  currencySymbol,
  cashInputValue,
  beginCashEdit,
  changeCashEdit,
  endCashEdit,
  chipInputValue,
  beginChipEdit,
  changeChipEdit,
  endChipEdit,
  updatePlayerName,
  removePlayer,
}: PlayerBuyInRowProps) {
  return (
    <div className="grid gap-2 grid-cols-[1.75rem_minmax(0,1fr)_5.25rem_5.25rem] md:grid-cols-[1.75rem_2fr_1fr_1fr] md:gap-3 items-center">
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        className="rounded-full"
        onClick={() => removePlayer(player.id)}
        aria-label={`Remove ${player.name || `player ${rowLabel}`}`}
        title="Remove player"
      >
        <RemoveIcon />
      </Button>

      <Label htmlFor={`name-${player.id}`} className="sr-only">
        Player {rowLabel} name
      </Label>
      <Input
        id={`name-${player.id}`}
        value={player.name}
        onChange={(e) => updatePlayerName(player.id, e.target.value)}
        placeholder="Player name"
        className="h-8 px-2 text-xs md:h-9 md:px-3 md:text-sm"
      />

      <Label htmlFor={`cashin-${player.id}`} className="sr-only">
        Player {rowLabel} cash-in
      </Label>
      <div className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs md:left-3 md:text-sm">
          {currencySymbol}
        </span>
        <Input
          id={`cashin-${player.id}`}
          type="text"
          inputMode="decimal"
          value={cashInputValue(player.id, player.cashIn)}
          onFocus={() => beginCashEdit(player.id, player.cashIn)}
          onChange={(e) => changeCashEdit(player.id, e.target.value)}
          onBlur={endCashEdit}
          placeholder={`0${decimalSeparator}00`}
          className="pl-4 pr-2 h-8 text-xs md:pl-7 md:pr-3 md:h-9 md:text-sm"
        />
      </div>

      <Label htmlFor={`chipsbought-${player.id}`} className="sr-only">
        Player {rowLabel} chips bought
      </Label>
      <Input
        id={`chipsbought-${player.id}`}
        type="text"
        inputMode="numeric"
        value={chipInputValue(player.id, "chipsBought", player.chipsBought)}
        onFocus={() => beginChipEdit(player.id, "chipsBought", player.chipsBought)}
        onChange={(e) => changeChipEdit(player.id, "chipsBought", e.target.value)}
        onBlur={() => endChipEdit(player.id, "chipsBought")}
        placeholder="0"
        className="h-8 px-2 text-xs md:h-9 md:px-3 md:text-sm"
      />
    </div>
  );
}
